import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

// Hive renders asynchronously into a shadow root, so it counts as loaded once the widget has a visible size.
const HIVE_TIMEOUT_MS = 2500;

// Hive's inner text element adds its own offset and spacing, which the fallback notice does not have.
const hiveResetStyles = new CSSStyleSheet();
hiveResetStyles.replaceSync('delivery-promise-text { top: 0; margin: 0; }');

/**
 * Keeps the delivery notice in sync with the selected variant and falls back to it
 * when the Hive Delivery Promise widget does not render (blocked, no data, error).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} status
 * @property {HTMLElement} app
 * @property {HTMLTemplateElement} unavailableTemplate
 *
 * @extends {Component<Refs>}
 */
class DeliveryInfo extends Component {
  requiredRefs = ['status', 'app', 'unavailableTemplate'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #hiveTimedOut = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    // Same scoping as Horizon's product-inventory component
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#watchHive();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  /** Observes the app block wrappers, since the inline widget itself never reports resizes. */
  #watchHive() {
    this.#resizeObserver = new ResizeObserver(() => this.#updateHiveState());
    for (const appBlock of this.refs.app.children) this.#resizeObserver.observe(appBlock);

    setTimeout(() => {
      this.#hiveTimedOut = true;
      this.#updateHiveState();
    }, HIVE_TIMEOUT_MS);

    this.#updateHiveState();
  }

  #getWidget() {
    return this.refs.app.querySelector('delivery-promise-widget');
  }

  /** Sets data-hive to 'ready', 'pending' or 'failed'. */
  #updateHiveState() {
    // A hidden app slot has no size, so only measure while Hive is supposed to show
    if (this.dataset.state !== 'shipping') return;

    const widget = this.#getWidget();
    const hasContent = Boolean(widget && widget.getBoundingClientRect().height > 0);

    if (hasContent) {
      this.dataset.hive = 'ready';
      this.#resetHiveStyles(widget);
    } else {
      this.dataset.hive = this.#hiveTimedOut ? 'failed' : 'pending';
    }
  }

  /**
   * Adopts the reset styles into Hive's open shadow root, runs before paint via the ResizeObserver.
   * @param {Element | null} widget
   */
  #resetHiveStyles(widget) {
    const root = widget?.shadowRoot;
    if (!root || root.adoptedStyleSheets.includes(hiveResetStyles)) return;

    root.adoptedStyleSheets = [...root.adoptedStyleSheets, hiveResetStyles];
  }

  /**
   * Takes the notice and state of this block from the re-rendered section.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Ignore swatch changes inside product cards on the same section
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, resource } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // resource === null means the option combination does not exist
        if (!resource) {
          this.dataset.state = 'unavailable';
          this.refs.status.replaceChildren(this.refs.unavailableTemplate.content.cloneNode(true));
          return;
        }

        const newBlock = html.querySelector(`lc-delivery-info[data-block-id="${this.dataset.blockId}"]`);
        const newStatus = newBlock?.querySelector('[ref="status"]');
        if (!(newBlock instanceof HTMLElement) || !newStatus) return;

        // The app slot is left untouched, Hive updates its widget on variant change itself
        morph(this.refs.status, newStatus, { childrenOnly: true });
        this.dataset.state = newBlock.dataset.state;
        this.#updateHiveState();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[delivery-info] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('lc-delivery-info')) {
  customElements.define('lc-delivery-info', DeliveryInfo);
}
